<template>
 <div class="message-history-container">
   <!-- Header Section -->
   <div class="header-section">
     <div class="page-header">
       <h1 class="page-title">
         <v-icon class="title-icon" color="#fff">mdi-message-text-outline</v-icon>
         Message History
       </h1>
       <p class="page-subtitle">View your SMS and Email communication history</p>
     </div>


     <!-- Filters Section -->
     <v-card class="filter-card" elevation="0" outlined>
       <v-card-text class="pa-4">
         <v-row align="center" no-gutters>
           <v-col cols="12" sm="6" md="3" class="pr-sm-2">
             <v-select
               v-model="selectedType"
               :items="typeOptions"
               label="Message Type"
               dense
               outlined
               hide-details
               prepend-inner-icon="mdi-filter-variant"
               @change="filterLogs"
               clearable
             >
               <template v-slot:selection="{ item }">
                 <v-chip small :color="getTypeColor(item)" text-color="white">
                   <v-icon left small>{{ getTypeIcon(item) }}</v-icon>
                   {{ item }}
                 </v-chip>
               </template>
             </v-select>
           </v-col>
          
           <v-col cols="12" sm="6" md="3" class="px-sm-2">
             <v-menu
               v-model="dateMenu"
               :close-on-content-click="false"
               transition="scale-transition"
               offset-y
               max-width="290px"
               min-width="290px"
             >
               <template v-slot:activator="{ on, attrs }">
                 <v-text-field
                   v-model="selectedDate"
                   label="Filter by Date"
                   prepend-inner-icon="mdi-calendar"
                   readonly
                   dense
                   outlined
                   hide-details
                   v-bind="attrs"
                   v-on="on"
                   clearable
                   @click:clear="clearDateFilter"
                 ></v-text-field>
               </template>
               <v-date-picker
                 v-model="selectedDate"
                 @input="dateMenu = false; filterLogs()"
                 no-title
                 scrollable
               ></v-date-picker>
             </v-menu>
           </v-col>


           <v-col cols="12" sm="6" md="4" class="px-sm-2">
             <v-text-field
               v-model="searchQuery"
               label="Search messages..."
               prepend-inner-icon="mdi-magnify"
               dense
               outlined
               hide-details
               @input="filterLogs"
               clearable
             ></v-text-field>
           </v-col>


           <v-col cols="12" sm="6" md="2" class="pl-sm-2">
             <v-btn
               @click="clearAllFilters"
               outlined
               color="primary"
               block
               class="filter-clear-btn"
             >
               <v-icon left>mdi-filter-remove</v-icon>
               Clear
             </v-btn>
           </v-col>
         </v-row>
       </v-card-text>
     </v-card>
   </div>


   <!-- Loading Skeleton -->
   <div v-if="loading" class="skeleton-container">
     <v-card
       v-for="n in 6"
       :key="n"
       class="message-skeleton mb-4"
       elevation="0"
       outlined
     >
       <v-card-text>
         <div class="d-flex justify-space-between mb-3">
           <div class="d-flex align-center">
             <v-skeleton-loader type="chip" width="80"></v-skeleton-loader>
             <v-skeleton-loader type="text" width="150" class="ml-3"></v-skeleton-loader>
           </div>
           <v-skeleton-loader type="text" width="100"></v-skeleton-loader>
         </div>
         <v-skeleton-loader type="heading" class="mb-2"></v-skeleton-loader>
         <v-skeleton-loader type="paragraph"></v-skeleton-loader>
       </v-card-text>
     </v-card>
   </div>


   <!-- No Data State -->
   <div v-else-if="!loading && filteredLogs.length === 0" class="no-data-container">
     <div class="no-data-content">
       <v-icon size="120" color="grey lighten-2" class="mb-4">
         {{ logs.length === 0 ? 'mdi-message-outline' : 'mdi-filter-remove-outline' }}
       </v-icon>
       <h3 class="no-data-title">
         {{ logs.length === 0 ? 'No Messages Found' : 'No Results Found' }}
       </h3>
       <p class="no-data-subtitle">
         {{ logs.length === 0
           ? 'Your message history will appear here once you start sending SMS or emails.'
           : 'Try adjusting your filters to find what you\'re looking for.'
         }}
       </p>
       <v-btn
         v-if="logs.length > 0"
         @click="clearAllFilters"
         color="primary"
         outlined
         class="mt-4"
       >
         <v-icon left>mdi-filter-remove</v-icon>
         Clear All Filters
       </v-btn>
     </div>
   </div>


   <!-- Messages List -->
   <div v-else class="messages-container">
     <v-card
       v-for="log in paginatedLogs"
       :key="log._id"
       class="message-card mb-4"
       elevation="0"
       outlined
       hover
       @click="goToDetails(log)"
     >
       <v-card-text class="pa-0">
         <div class="message-header">
           <div class="d-flex align-center flex-grow-1">
             <v-chip
               small
               :color="getTypeColor(log.type)"
               text-color="white"
               class="message-type-chip"
             >
               <v-icon left small>{{ getTypeIcon(log.type) }}</v-icon>
               {{ log.type }}
             </v-chip>
            
             <div class="recipients ml-3">
               <span class="recipients-label">To:</span>
               <div class="recipients-list">
                 <v-chip
                   v-for="(recipient, index) in log.to.slice(0, 3)"
                   :key="index"
                   x-small
                   outlined
                   class="recipient-chip mr-1 mb-1"
                 >
                   {{ recipient }}
                 </v-chip>
                 <v-chip
                   v-if="log.to.length > 3"
                   x-small
                   outlined
                   color="grey"
                   class="more-recipients"
                 >
                   +{{ log.to.length - 3 }} more
                 </v-chip>
               </div>
             </div>
           </div>
          
           <div class="message-date">
             <v-icon small color="grey">mdi-clock-outline</v-icon>
             <span class="date-text">{{ formattedDate(log.createdAt) }}</span>
           </div>
         </div>


         <div class="message-content">
           <h4 class="message-subject">{{ log.subject || 'No Subject' }}</h4>
           <p class="message-preview">{{ extractContent(log.content) }}</p>
         </div>


         <div class="message-actions">
           <v-btn
             small
             text
             color="primary"
             class="view-details-btn"
           >
             <v-icon left small>mdi-eye-outline</v-icon>
             View Details
           </v-btn>
         </div>
       </v-card-text>
     </v-card>


     <!-- Pagination -->
     <div v-if="totalPages > 1" class="pagination-container">
       <v-pagination
         v-model="currentPage"
         :length="totalPages"
         :total-visible="7"
         color="primary"
         class="custom-pagination"
       ></v-pagination>
     </div>
   </div>


   <!-- Snackbar -->
   <v-snackbar
     v-model="snackbar"
     :timeout="4000"
     bottom
     :color="snackbarColor"
   >
     <v-icon left>{{ snackbarIcon }}</v-icon>
     {{ snackMessage }}
     <template v-slot:action="{ attrs }">
       <v-btn text v-bind="attrs" @click="snackbar = false">
         Close
       </v-btn>
     </template>
   </v-snackbar>
 </div>
</template>


<script>
import dateFormat from "dateformat";
import { mapActions, mapGetters } from "vuex";


export default {
 name: "MessageHistory",
 data() {
   return {
     loading: false,
     snackbar: false,
     snackMessage: null,
     snackbarColor: 'success',
     snackbarIcon: 'mdi-check-circle',
    
     // Filter states
     selectedType: null,
     selectedDate: null,
     searchQuery: '',
     dateMenu: false,
    
     // Pagination
     currentPage: 1,
     itemsPerPage: 10,
    
     // Filter options
     typeOptions: ['SMS', 'EMAIL', 'PUSH'],
     filteredLogs: []
   };
 },
 methods: {
   ...mapActions({
     getAllLogs: "messagelog/fetchAllLogs"
   }),
  
   extractContent(content) {
     if (!content) return 'No content available';
     const span = document.createElement("span");
     span.innerHTML = content;
     const text = span.textContent || span.innerText;
     return text.length > 150 ? text.substring(0, 150) + '...' : text;
   },
  
   formattedDate(dateStr) {
     if (!dateStr) return '';
     const date = new Date(dateStr);
     const convertedDate = this.convertTZ(date, this.timezone);
     const today = new Date();
     const isToday = convertedDate.toDateString() === today.toDateString();
    
     if (isToday) {
       return dateFormat(convertedDate, "h:MM tt");
     }
    
     const yesterday = new Date(today);
     yesterday.setDate(yesterday.getDate() - 1);
     const isYesterday = convertedDate.toDateString() === yesterday.toDateString();
    
     if (isYesterday) {
       return "Yesterday " + dateFormat(convertedDate, "h:MM tt");
     }
    
     return dateFormat(convertedDate, "mmm d, yyyy h:MM tt");
   },
  
   convertTZ(date, tzString) {
     if (!date) return new Date();
     return new Date(
       (typeof date === "string" ? new Date(date) : date).toLocaleString(
         "en-US",
         {
           timeZone: tzString || "America/New_York"
         }
       )
     );
   },
  
    goToDetails(log) {
      this.$router.push({
        name: "agency-message",
        query: { category: log.type, _id: log._id }
      });
    },
  
   showSnackMessage(message, type = 'success') {
     this.snackMessage = message;
     this.snackbarColor = type;
     this.snackbarIcon = type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle';
     this.snackbar = true;
   },
  
   getTypeColor(type) {
     const colors = {
       'SMS': 'green',
       'EMAIL': 'blue',
       'PUSH': 'orange'
     };
     return colors[type?.toUpperCase()] || 'grey';
   },
  
   getTypeIcon(type) {
     const icons = {
       'SMS': 'mdi-message-text',
       'EMAIL': 'mdi-email',
       'PUSH': 'mdi-bell'
     };
     return icons[type?.toUpperCase()] || 'mdi-message';
   },
  
   filterLogs() {
     let filtered = [...this.logs];
    
     // Filter by type
     if (this.selectedType) {
       filtered = filtered.filter(log =>
         log.type?.toLowerCase() === this.selectedType.toLowerCase()
       );
     }
    
     // Filter by date
     if (this.selectedDate) {
       filtered = filtered.filter(log => {
         const logDate = new Date(log.createdAt).toISOString().split('T')[0];
         return logDate === this.selectedDate;
       });
     }
    
     // Filter by search query
     if (this.searchQuery) {
       const query = this.searchQuery.toLowerCase();
       filtered = filtered.filter(log =>
         log.subject?.toLowerCase().includes(query) ||
         log.content?.toLowerCase().includes(query) ||
         log.to?.some(recipient => recipient.toLowerCase().includes(query))
       );
     }
    
     this.filteredLogs = filtered;
     this.currentPage = 1; // Reset to first page when filtering
   },
  
   clearAllFilters() {
     this.selectedType = null;
     this.selectedDate = null;
     this.searchQuery = '';
     this.filterLogs();
   },
  
   clearDateFilter() {
     this.selectedDate = null;
     this.filterLogs();
   }
 },
  computed: {
   ...mapGetters({
     profile: "auth/getProfile",
     logs: "messagelog/getAllLogs",
     timezone: "usersetting/getTimeZone"
   }),
  
   paginatedLogs() {
     const start = (this.currentPage - 1) * this.itemsPerPage;
     const end = start + this.itemsPerPage;
     return this.filteredLogs.slice(start, end);
   },
  
   totalPages() {
     return Math.ceil(this.filteredLogs.length / this.itemsPerPage);
   }
 },
  watch: {
   logs: {
     handler() {
       this.filterLogs();
     },
     immediate: true
   },
  
   profile(newValue) {
     if (newValue) {
       this.email = newValue.email;
       this.phone = newValue.phone;
     }
   }
 },
  mounted() {
   this.loading = true;
   this.getAllLogs()
     .then(() => {
       this.loading = false;
       this.showSnackMessage('Message history loaded successfully');
     })
     .catch(error => {
       this.loading = false;
       console.error('Error loading logs:', error);
       this.showSnackMessage(
         error?.response?.data?.message || 'Failed to load message history',
         'error'
       );
     });
 }
};
</script>


<style lang="scss" scoped>
.message-history-container {
 max-width: 1200px;
 margin: 0 auto;
 padding: 16px;
  @media (max-width: 600px) {
   padding: 8px;
 }
}


.header-section {
 margin-bottom: 24px;
 background:linear-gradient(135deg,#084468,#3b7ca1);
     padding: 20px 20px;
   border-radius: 20px;
   border: 1px solid #ffff;
}


.page-header {
 margin-bottom: 20px;
 text-align: center;
  @media (min-width: 960px) {
   text-align: left;
 }
}


.page-title {
 font-size: 2rem;
 font-weight: 600;
 color: #fff;
 margin-bottom: 8px;
 display: flex;
 align-items: center;
 justify-content: center;
  @media (min-width: 960px) {
   justify-content: flex-start;
 }
}


.title-icon {
 margin-right: 12px;
 font-size: 2rem;
}


.page-subtitle {
 color: #fff;
 font-size: 1rem;
 margin: 0;
}


.filter-card {
 border-radius: 12px;
 border: 1px solid #e0e0e0;
 background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}


.filter-clear-btn {
 height: 40px;
}


.skeleton-container {
 .message-skeleton {
   border-radius: 12px;
   background: #fafafa;
 }
}


.no-data-container {
 display: flex;
 justify-content: center;
 align-items: center;
 min-height: 400px;
 text-align: center;
}


.no-data-content {
 max-width: 400px;
}


.no-data-title {
 color: #ffff;
 font-weight: 500;
 margin-bottom: 12px;
}


.no-data-subtitle {
 color: #ffff;
 line-height: 1.5;
 margin-bottom: 0;
}


.messages-container {
 .message-card {
   border-radius: 12px;
   border: 1px solid #e0e0e0;
   transition: all 0.3s ease;
   cursor: pointer;
   background: #ffffff;
  
   &:hover {
     border-color: #1976d2;
     box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
     transform: translateY(-2px);
   }
 }
}


.message-header {
 display: flex;
 justify-content: space-between;
 align-items: flex-start;
 padding: 16px 16px 0;
 gap: 16px;
  @media (max-width: 600px) {
   flex-direction: column;
   gap: 8px;
 }
}


.message-type-chip {
 font-weight: 600;
 border-radius: 16px;
}


.recipients {
 flex-grow: 1;
 min-width: 0;
}


.recipients-label {
 font-size: 0.875rem;
 color: #666;
 font-weight: 500;
}


.recipients-list {
 margin-top: 4px;
 display: flex;
 flex-wrap: wrap;
 gap: 4px;
}


.recipient-chip {
 font-size: 0.75rem;
 height: 24px;
}


.more-recipients {
 font-size: 0.75rem;
 height: 24px;
}


.message-date {
 display: flex;
 align-items: center;
 gap: 4px;
 color: #666;
 font-size: 0.875rem;
 white-space: nowrap;
}


.message-content {
 padding: 8px 16px;
}


.message-subject {
 font-size: 1.1rem;
 font-weight: 600;
 color: #333;
 margin-bottom: 8px;
 line-height: 1.3;
}


.message-preview {
 color: #666;
 font-size: 0.875rem;
 line-height: 1.4;
 margin: 0;
 display: -webkit-box;
 -webkit-line-clamp: 2;
 -webkit-box-orient: vertical;
 overflow: hidden;
}


.message-actions {
 padding: 0 16px 16px;
 display: flex;
 justify-content: flex-end;
}


.view-details-btn {
 font-weight: 500;
 text-transform: none;
}


.pagination-container {
 display: flex;
 justify-content: center;
 margin-top: 32px;
 padding: 16px 0;
}


.custom-pagination {
 ::v-deep .v-pagination__item {
   border-radius: 8px;
   margin: 0 2px;
 }
  ::v-deep .v-pagination__item--active {
   background: #1976d2 !important;
   color: white;
 }
}


// Responsive adjustments
@media (max-width: 600px) {
 .message-header {
   .recipients {
     order: 2;
   }
  
   .message-date {
     order: 1;
     align-self: flex-end;
   }
 }
  .filter-card {
   ::v-deep .v-card-text {
     padding: 12px !important;
   }
 }
  .page-title {
   font-size: 1.5rem;
 }
}


// Dark theme support
// @media (prefers-color-scheme: dark) {
//   .filter-card {
//     background: linear-gradient(135deg, #2c2c2c 0%, #1e1e1e 100%);
//   }
 //   .message-card {
//     background: #1e1e1e;
//     border-color: #333;
  
//     &:hover {
//       border-color: #64b5f6;
//       box-shadow: 0 4px 12px rgba(100, 181, 246, 0.15);
//     }
//   }
// }
</style>





